import React from 'react'
import {
  getIntervalFundData,
  getDownloadCapGains,
} from '../InvestmentProductsDataSource.js'
import DOMPurify from 'dompurify'
import { encodeForURL } from '../../../../global/formatters/string-formatter.js'
import * as StringFormatter from '../../../../global/formatters/string-formatter.js'
import { getRelativeBasePath } from '../../../../global/environments/path.js'
import {
  hasFundDistributionsEstimates,
  formatToFixed,
} from '../helpers/InvestmentProductsHelper.js'

const sortCapitalGains = (funds) => {
  funds?.forEach((fund) => {
    fund?.distributions?.sort((a, b) => {
      return a.recordDate.localeCompare(b.recordDate)
    })
  })

  return funds
}

export const getDataKey = (key) => {
  switch (key) {
    case '#capital-gains':
      return 'capitalGains'
    case '#prices-yields':
      return 'priceFunds'
    case '#morningstar-ratings':
      return 'morningStarFunds'
    default:
      return 'performanceFunds'
  }
}

export const DEFAULT_CLASS = 'Institutional'
export const getTabData = async (dataType) => {
  const getClassAData = await getIntervalFundData(dataType)
  return getClassAData?.value?.intervalFunds
}
const getFundClass = (fund) => {
  return fund.fundClass || DEFAULT_CLASS
}
export const getAspect = (
  section,
  selectedSort = '',
  selectedPerformanceType = '',
  fundRoot = {},
  selectedSortDirection = ''
) => {
  const _aspects = [
    {
      name: '#performance',
      info: {
        tag: 'Monthly',
        selector: [
          {
            name: 'performanceFunds',
            isArray: false,
          },
        ],
        headerSummary1: () => {
          return (
            <>
              Average Annual Total Returns (%) as of{' '}
              {StringFormatter.formatDateStandard(fundRoot.navAsOfDate)}
            </>
          )
        },
        headerSummary2: () => {
          return (
            <>
              Expense Ratio as of{' '}
              {StringFormatter.formatDateStandard(fundRoot.expenseAsOfDate)}
            </>
          )
        },
        colSpan1: 3,
        colSpan2: 6,
        colSpan3: 3,
        columns: [
          {
            name: 'fundName',
            className:
              'Table-header sorting' +
              (selectedSort === 'fundName'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Fund Name: activate to sort column descending',
            content: <>Name</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'fundName' ? ' Icon--before' : ''),
              sort: 'fundName',
              sortType: 'string',
            },
            cell: {
              content: (fund) => (
                <a
                  className="u-textBold u-flex u-flexAlignItemsBaseline"
                  href={DOMPurify.sanitize(
                    getRelativeBasePath() +
                      `/investment-products/interval-funds/${StringFormatter.urlSafe(
                        fund.fundName
                      )}/Class-${getFundClass(fund)}/details/?cusip=${
                        fund.cusip
                      }`
                  )}
                >
                  {fund.fundName}
                </a>
              ),
            },
          },
          {
            name: 'class',
            className:
              'Table-header sorting ' +
              (selectedSort === 'fundClass'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Class',
            content: <>Class</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight4gu' +
                (selectedSort === 'fundClass' ? ' Icon--before' : ''),
              sort: 'fundClass',
              sortType: 'string',
            },
            cell: {
              content: (fund) => <>Class {fund.fundClass}</>,
            },
          },
          {
            name: 'ytd',
            className:
              'Table-header u-textRight sorting bordered' +
              (selectedSort === 'ytdPercent' || selectedSort === 'ytdPercentPOP'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel:
              'YTD % as of ' +
              StringFormatter.formatDateStandard(fundRoot.ytdAsOfDate) +
              ': activate to sort column',
            content: (
              <>
                YTD % as of
                <br />
                {StringFormatter.formatDateStandard(fundRoot.ytdAsOfDate)}
              </>
            ),
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'ytdPercent' ||
                selectedSort === 'ytdPercentPOP'
                  ? ' Icon--before'
                  : ''),
              sort:
                selectedPerformanceType === 'nav'
                  ? 'ytdPercent'
                  : 'ytdPercentPOP',
              sortType: 'number',
            },
            cell: {
              className: ' bordered-right u-textRight',
              content: (fund) =>
                selectedPerformanceType === 'nav'
                  ? fund.ytdPercent
                  : fund.ytdPercentPOP,
            },
          },
          {
            name: '1year',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'navOneYear' || selectedSort === 'popOneYear'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: '1 Year: activate to sort column',
            content: <>1 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navOneYear' || selectedSort === 'popOneYear'
                  ? ' Icon--before'
                  : ''),
              sort:
                selectedPerformanceType === 'nav' ? 'navOneYear' : 'popOneYear',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) =>
                selectedPerformanceType === 'nav'
                  ? fund.navOneYear
                  : fund.popOneYear,
            },
          },
          {
            name: '3year',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'navThreeYear' ||
              selectedSort === 'popThreeYear'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: '3 Year: activate to sort column',
            content: <>3 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navThreeYear' ||
                selectedSort === 'popThreeYear'
                  ? ' Icon--before'
                  : ''),
              sort:
                selectedPerformanceType === 'nav'
                  ? 'navThreeYear'
                  : 'popThreeYear',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) =>
                selectedPerformanceType === 'nav'
                  ? fund.navThreeYear
                  : fund.popThreeYear,
            },
          },
          {
            name: '5year',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'navFiveYear' || selectedSort === 'popFiveYear'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: '5 Year: activate to sort column',
            content: <>5 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navFiveYear' ||
                selectedSort === 'popFiveYear'
                  ? ' Icon--before'
                  : ''),
              sort:
                selectedPerformanceType === 'nav'
                  ? 'navFiveYear'
                  : 'popFiveYear',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) =>
                selectedPerformanceType === 'nav'
                  ? fund.navFiveYear
                  : fund.popFiveYear,
            },
          },
          {
            name: '10year',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'navTenYear' || selectedSort === 'popTenYear'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: '10 Year: activate to sort column',
            content: <>10 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navTenYear' || selectedSort === 'popTenYear'
                  ? ' Icon--before'
                  : ''),
              sort:
                selectedPerformanceType === 'nav' ? 'navTenYear' : 'popTenYear',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) =>
                selectedPerformanceType === 'nav'
                  ? fund.navTenYear
                  : fund.popTenYear,
            },
          },
          {
            name: 'inception',
            className:
              'Table-header u-textNoWrap u-textRight sorting' +
              (selectedSort === 'navSinceInception' ||
              selectedSort === 'popSinceInception'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Since Fund Inception*: activate to sort column',
            content: (
              <>
                Since Fund
                <br />
                Incept.
              </>
            ),
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navSinceInception' ||
                selectedSort === 'popSinceInception'
                  ? ' Icon--before'
                  : ''),
              sort:
                selectedPerformanceType === 'nav'
                  ? 'navSinceInception'
                  : 'popSinceInception',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) =>
                selectedPerformanceType === 'nav'
                  ? fund.navSinceInception
                  : fund.popSinceInception,
            },
          },
          {
            name: 'inceptionDt',
            className:
              'Table-header u-textNoWrap u-textRight sorting bordered' +
              (selectedSort === 'inceptionDt'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Fund Inception Date: activate to sort column',
            content: (
              <>
                Fund Incept.
                <br />
                Date
              </>
            ),
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'inceptionDt' ? ' Icon--before' : ''),
              sort: 'inceptionDt',
              sortType: 'date',
            },
            cell: {
              className: ' u-textNoWrap bordered-right u-textRight',
              content: (fund) =>
                StringFormatter.formatDateStandard(fund.inceptionDt),
            },
          },
          {
            name: 'gross',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'grossExp'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Net: activate to sort column',
            content: <>Gross</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'grossExp' ? ' Icon--before' : ''),
              sort: 'grossExp',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.grossExp,
            },
          },
          {
            name: 'net',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'netExp'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Gross: activate to sort column',
            content: <>Net</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'netExp' ? ' Icon--before' : ''),
              sort: 'netExp',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.netExp,
            },
          },
          {
            name: 'waiverDt',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'waiverDt'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Fund Waiver Expiration Date: activate to sort column',
            content: (
              <>
                Waiver Expiration
                <br />
                Date
              </>
            ),
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'waiverDt' ? ' Icon--before' : ''),
              sort: 'waiverDt',
              sortType: 'date',
            },
            cell: {
              className: ' u-textNoWrap u-textRight',
              content: (fund) =>
                StringFormatter.formatDateStandard(fund.expenseRatioWaiverDate),
            },
          },
        ],
      },
    },
    {
      name: '#prices-yields',
      info: {
        tag: 'PricesAndYields',
        selector: [
          {
            name: 'priceFunds',
            isArray: false,
          },
        ],
        headerSummary1: () => {
          return (
            <>
              Prices as of{' '}
              {StringFormatter.formatDateStandard(fundRoot.priceAsOfDate)}
            </>
          )
        },
        headerSummary2: () => {
          return (
            <>
              30-day SEC Yield as of{' '}
              {StringFormatter.formatDateStandard(fundRoot.secYldDate)}
            </>
          )
        },
        colSpan1: 2,
        colSpan2: 4,
        colSpan3: 2,
        columns: [
          {
            name: 'fundName',
            className:
              'Table-header sorting' +
              (selectedSort === 'fundName' || selectedSort === 'symbol'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Fund Name: activate to sort column descending',
            content: <>Name</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'fundName' || selectedSort === 'symbol'
                  ? ' Icon--before'
                  : ''),
              sort: 'fundName',
              sortType: 'string',
            },
            cell: {
              className: '',
              content: (fund) => (
                <a
                  className="u-textBold u-flex u-flexAlignItemsBaseline"
                  href={DOMPurify.sanitize(
                    getRelativeBasePath() +
                      `/investment-products/interval-funds/${StringFormatter.urlSafe(
                        fund.fundName
                      )}/Class-${getFundClass(fund)}/details/?cusip=${
                        fund.cusip
                      }`
                  )}
                >
                  {fund.fundName}
                </a>
              ),
            },
          },
          {
            name: 'class',
            className:
              'Table-header sorting bordered' +
              (selectedSort === 'fundClass'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Class',
            content: <>Class</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight4gu' +
                (selectedSort === 'fundClass' ? ' Icon--before' : ''),
              sort: 'fundClass',
              sortType: 'string',
            },
            cell: {
              className: ' bordered-right',
              content: (fund) => <>Class {fund.fundClass}</>,
            },
          },
          {
            name: 'navDollar',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'navDollar'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Nav Amount: activate to sort column',
            content: <>Nav $</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navDollar' ? ' Icon--before' : ''),
              sort: 'navDollar',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.navDollar,
            },
          },
          {
            name: 'popDollar',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'popDollar'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'POP Amount: activate to sort column',
            content: <>POP $</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'popDollar' ? ' Icon--before' : ''),
              sort: 'popDollar',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.popDollar,
            },
          },
          {
            name: 'navDollarChange',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'navDollarChange'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Nav Change Amount: activate to sort column',
            content: <>NAV chg ($)</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navDollarChange' ? ' Icon--before' : ''),
              sort: 'navDollarChange',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.navDollarChange,
            },
          },
          {
            name: 'navPercentageChange',
            className:
              'Table-header u-textRight sorting bordered' +
              (selectedSort === 'navPercentageChange'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'NAV Change Percentage: activate to sort column',
            content: <>NAV chg (%)</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'navPercentageChange' ? ' Icon--before' : ''),
              sort: 'navPercentageChange',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight bordered-right',
              content: (fund) => fund.navPercentageChange,
            },
          },
          {
            name: 'secYield',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'secYield'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'SEC Yield Percentage: activate to sort column',
            content: <>SEC Yield (%)</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'secYield' ? ' Icon--before' : ''),
              sort: 'secYield',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.secYield,
            },
          },
          {
            name: 'secYldUnsubsidized',
            className:
              'Table-header u-textRight sorting' +
              (selectedSort === 'secYldUnsubsidized'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel:
              'SEC Yield Unsubsidized Percentage: activate to sort column',
            content: (
              <>
                Unsubsidized
                <br />
                SEC Yield (%)
              </>
            ),
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight' +
                (selectedSort === 'secYldUnsubsidized' ? ' Icon--before' : ''),
              sort: 'secYldUnsubsidized',
              sortType: 'number',
            },
            cell: {
              className: ' u-textRight',
              content: (fund) => fund.secYldUnsubsidized,
            },
          },
        ],
      },
    },
    {
      name: '#morningstar-ratings',
      info: {
        tag: '1,2,3,4,5',
        selector: [
          {
            name: 'morningStarFunds',
            isArray: false,
          },
        ],
        headerSummary1: (funds) => {
          return (
            <>
              Morningstar Rating as of{' '}
              {(funds.length > 0 &&
                (funds[0].asOfDate
                  ? StringFormatter.formatDateStandard(funds[0].asOfDate)
                  : '-')) ||
                ''}
              <p className="u-paddingTop2gu u-textSecondary u-textNormal u-textSizeMsrm1">
                (based on a Morningstar Risk-Adjusted Return measure)
              </p>
            </>
          )
        },
        headerSummary2: () => null,
        colSpan1: 2,
        colSpan2: 4,
        colSpan3: 1,
        columns: [
          {
            name: 'fundName',
            className:
              'Table-header sorting' +
              (selectedSort === 'fundName' || selectedSort === 'symbol'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Fund Name: activate to sort column descending',
            content: <>Name</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'fundName' || selectedSort === 'symbol'
                  ? ' Icon--before'
                  : ''),
              sort: 'fundName',
              sortType: 'string',
            },
            cell: {
              className: '',
              content: (fund) => (
                <a
                  className="u-textBold u-flex u-flexAlignItemsBaseline"
                  href={DOMPurify.sanitize(
                    getRelativeBasePath() +
                      `/investment-products/interval-funds/${StringFormatter.urlSafe(
                        fund.fundName
                      )}/Class-${getFundClass(fund)}/details/?cusip=${
                        fund.cusip
                      }`
                  )}
                >
                  {fund.fundName}
                </a>
              ),
            },
          },
          {
            name: 'class',
            className:
              'Table-header sorting bordered' +
              (selectedSort === 'fundClass'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Class',
            content: <>Class</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight4gu' +
                (selectedSort === 'fundClass' ? ' Icon--before' : ''),
              sort: 'fundClass',
              sortType: 'string',
            },
            cell: {
              className: ' bordered-right',
              content: (fund) => <>Class {fund.fundClass}</>,
            },
          },
          {
            name: 'msOverallRating',
            className:
              'Table-header sorting' +
              (selectedSort === 'msOverallRating'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Morningstar Overall Rating: activate to sort column',
            content: <>Overall</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'msOverallRating' ? ' Icon--before' : ''),
              sort: 'msOverallRating',
              sortType: 'number',
              sortDirection: false,
            },
            cell: {
              content: (fund) =>
                renderStars(fund.msOverallRating, fund.overallFunds),
            },
          },
          {
            name: 'threeYearRating',
            className:
              'Table-header sorting' +
              (selectedSort === 'threeYearRating'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Morningstar 3 Year Rating: activate to sort column',
            content: <>3 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'threeYearRating' ? ' Icon--before' : ''),
              sort: 'threeYearRating',
              sortType: 'number',
              sortDirection: false,
            },
            cell: {
              content: (fund) =>
                renderStars(fund.threeYearRating, fund.threeYearFunds),
            },
          },
          {
            name: 'fiveYearRating',
            className:
              'Table-header sorting' +
              (selectedSort === 'fiveYearRating'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Morningstar 5 Year Rating: activate to sort column',
            content: <>5 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'fiveYearRating' ? ' Icon--before' : ''),
              sort: 'fiveYearRating',
              sortType: 'number',
              sortDirection: false,
            },
            cell: {
              content: (fund) =>
                renderStars(fund.fiveYearRating, fund.fiveYearFunds),
            },
          },
          {
            name: 'tenYearRating',
            className:
              'Table-header sorting bordered' +
              (selectedSort === 'tenYearRating'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Morningstar 10 Year Rating: activate to sort column',
            content: <>10 Year</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'tenYearRating' ? ' Icon--before' : ''),
              sort: 'tenYearRating',
              sortType: 'number',
              sortDirection: false,
            },
            cell: {
              className: ' bordered-right',
              content: (fund) =>
                renderStars(fund.tenYearRating, fund.tenYearFunds),
            },
          },
          {
            name: 'msCategoryName',
            className:
              'Table-header sorting' +
              (selectedSort === 'msCategoryName'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Morningstar Category: activate to sort column',
            content: <>Morningstar Category</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'msCategoryName' ? ' Icon--before' : ''),
              sort: 'msCategoryName',
              sortType: 'array',
            },
            cell: {
              content: (fund) =>
                fund.msCategoryName ? fund.msCategoryName : '-',
            },
          },
        ],
      },
    },
    {
      name: '#capital-gains',
      info: {
        tag: 'capGains',
        selector: [
          {
            name: 'capitalGains',
            isArray: false,
          },
        ],
        headerSummary1: () => {
          return <>Breakdown of Capital Gains Distributions and Estimates </>
        },
        headerSummary2: () => null,
        parseData: sortCapitalGains,
        colSpan1: 4,
        colSpan2: 3,
        colSpan3: null,
        columns: [
          {
            name: 'fundName',
            className:
              'Table-header sorting' +
              (selectedSort === 'fundName' || selectedSort === 'symbol'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Fund Name: activate to sort column descending',
            content: <>Fund Name</>,
            button: {
              className:
                'Table-headerNode u-flex u-padding2gu u-paddingRight3gu' +
                (selectedSort === 'fundName' || selectedSort === 'symbol'
                  ? ' Icon--before'
                  : ''),
              sort: 'fundName',
              sortType: 'string',
            },
            cell: {
              content: (fund) => (
                <a
                  className="u-textBold u-flex u-flexAlignItemsBaseline"
                  href={DOMPurify.sanitize(
                    getRelativeBasePath() +
                      `/investment-products/interval-funds/${StringFormatter.urlSafe(
                        fund.fundName
                      )}/Class-${
                        fund.className === 'Institutional'
                          ? fund.className
                          : 'A'
                      }/details/?cusip=${fund.cusip}`
                  )}
                >
                  {fund.fundName}
                </a>
              ),
            },
          },
          {
            name: 'class',
            className:
              'Table-header sorting' +
              (selectedSort === 'fundClass'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Class',
            content: <>Class</>,
            button: {
              className:
                'Table-headerNode u-padding2gu u-paddingRight4gu' +
                (selectedSort === 'fundClass' ? ' Icon--before' : ''),
              sort: 'fundClass',
              sortType: 'string',
            },
            cell: {
              className: ' u-textNoWrap',
              content: (fund) => (
                <>
                  Class{' '}
                  {fund.className === 'Institutional' ? fund.className : 'A'}
                </>
              ),
            },
          },
          {
            name: 'recordDate',
            className:
              'Table-header sorting u-paddingLeft0' +
              (selectedSort === 'recordDate'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Record Date: activate to sort column',
            content: <>Record Date</>,
            button: {
              className:
                'Table-headerNode u-flex u-paddingRight3gu u-paddingLeft0' +
                (selectedSort === 'recordDate' ? ' Icon--before' : ''),
              sort: 'recordDate',
              sortType: 'date',
            },
            cell: {
              className: ' u-padding0',
              content: (fund) => {
                const hasFundDistributions = hasFundDistributionsEstimates(
                  fund,
                  'distributions'
                )
                const hasFundEstimates = hasFundDistributionsEstimates(
                  fund,
                  'estimates'
                )

                return (
                  <div className="u-flex u-flexCol">
                    {hasFundDistributions && (
                      <div className="u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu">
                        <div>
                          <em>Distributions</em>
                        </div>
                        {fund.distributions &&
                          fund.distributions.map((value, index) => {
                            return (
                              <div className="u-paddingTop2gu" key={index}>
                                {StringFormatter.formatDateStandard(
                                  value.recordDate
                                )}
                              </div>
                            )
                          })}
                      </div>
                    )}
                    {hasFundEstimates && (
                      <div
                        className={`u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu ${
                          hasFundDistributions
                            ? 'u-borderColorNeutralLight u-borderTop'
                            : ''
                        }`}
                      >
                        <div>
                          <em>Estimates</em>
                        </div>
                        <div className="u-paddingTop2gu u-textNoWrap">
                          {StringFormatter.formatDateStandard(
                            fund?.estimates?.recordDate
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
          {
            name: 'payableDate',
            className:
              'Table-header sorting bordered u-textRight' +
              (selectedSort === 'exDivdndDate'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Ex/Payable Date: activate to sort column',
            content: <>Ex/Payable Date</>,
            button: {
              className:
                'Table-headerNode u-flex u-padding2gu u-paddingLeft3gu u-paddingRight3gu' +
                (selectedSort === 'exDivdndDate' ? ' Icon--before' : ''),
              sort: 'exDivdndDate',
              sortType: 'date',
            },
            cell: {
              className:
                ' u-paddingVert0 u-paddingLeft0 u-textNoWrap bordered-right u-textRight',
              content: (fund) => {
                const hasFundDistributions = hasFundDistributionsEstimates(
                  fund,
                  'distributions'
                )
                const hasFundEstimates = hasFundDistributionsEstimates(
                  fund,
                  'estimates'
                )

                return (
                  <div className="u-flex u-flexCol">
                    {hasFundDistributions && (
                      <div className="u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu">
                        <div>&nbsp;</div>
                        {fund.distributions &&
                          fund.distributions.map((value, index) => {
                            return (
                              <div className="u-paddingTop2gu" key={index}>
                                {StringFormatter.formatDateStandard(
                                  value.exDivdndDate
                                )}
                              </div>
                            )
                          })}
                      </div>
                    )}
                    {hasFundEstimates && (
                      <div
                        className={`u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu ${
                          hasFundDistributions
                            ? 'u-borderColorNeutralLight u-borderTop'
                            : ''
                        }`}
                      >
                        <div>&nbsp;</div>
                        <div className="u-paddingTop2gu">
                          {StringFormatter.formatDateStandard(
                            fund?.estimates?.exDivdndDate
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
          {
            name: 'shortTermCapGain',
            className:
              'Table-header u-textLeft sorting' +
              (selectedSort === 'shortTermCapGain'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Short-Term Capital Gains: activate to sort column',
            content: (
              <>
                Short-Term
                <br />
                Capital Gains ($)
              </>
            ),
            button: {
              className:
                'Table-headerNode u-flex u-padding2gu u-paddingRight3gu u-textLeft' +
                (selectedSort === 'shortTermCapGain' ? ' Icon--before' : ''),
              sort: 'shortTermCapGain',
              sortType: 'number',
            },
            cell: {
              className: ' u-paddingVert0 u-paddingRight0',
              content: (fund) => {
                const hasFundDistributions = hasFundDistributionsEstimates(
                  fund,
                  'distributions'
                )
                const hasFundEstimates = hasFundDistributionsEstimates(
                  fund,
                  'estimates'
                )

                return (
                  <div className="u-flex u-flexCol">
                    {hasFundDistributions && (
                      <div className="u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu">
                        <div>
                          <em>Distributions</em>
                        </div>
                        {fund.distributions &&
                          fund.distributions.map((value, index) => {
                            return (
                              <div className="u-paddingTop2gu" key={index}>
                                {formatToFixed(value.shortTermCapGain, 5)}
                              </div>
                            )
                          })}
                      </div>
                    )}
                    {hasFundEstimates && (
                      <div
                        className={`u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu ${
                          hasFundDistributions
                            ? 'u-borderColorNeutralLight u-borderTop'
                            : ''
                        }`}
                      >
                        <div>
                          <em>Estimates</em>
                        </div>
                        <div className="u-paddingTop2gu u-textNoWrap">
                          {formatToFixed(fund?.estimates?.shortTermCapGainSt)}{' '}
                          &minus;{' '}
                          {formatToFixed(fund?.estimates?.shortTermCapGainEnd)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
          {
            name: 'longTermCapGain',
            className:
              'Table-header u-textLeft sorting' +
              (selectedSort === 'longTermCapGain'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Long-Term Capital Gains: activate to sort column',
            content: (
              <>
                Long-Term Capital
                <br />
                Gains ($)
              </>
            ),
            button: {
              className:
                'Table-headerNode u-flex u-padding2gu u-paddingRight3gu u-textLeft' +
                (selectedSort === 'longTermCapGain' ? ' Icon--before' : ''),
              sort: 'longTermCapGain',
              sortType: 'number',
            },
            cell: {
              className: ' u-padding0',
              content: (fund) => {
                const hasFundDistributions = hasFundDistributionsEstimates(
                  fund,
                  'distributions'
                )
                const hasFundEstimates = hasFundDistributionsEstimates(
                  fund,
                  'estimates'
                )

                return (
                  <div className="u-flex u-flexCol">
                    {hasFundDistributions && (
                      <div className="u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu">
                        <div>&nbsp;</div>

                        {fund.distributions &&
                          fund.distributions.map((value, index) => {
                            return (
                              <div className="u-paddingTop2gu" key={index}>
                                {formatToFixed(value.longTermCapGain, 5)}
                              </div>
                            )
                          })}
                      </div>
                    )}
                    {hasFundEstimates && (
                      <div
                        className={`u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu ${
                          hasFundDistributions
                            ? 'u-borderColorNeutralLight u-borderTop'
                            : ''
                        }`}
                      >
                        <div>&nbsp;</div>
                        <div className="u-paddingTop2gu u-textNoWrap">
                          {formatToFixed(fund?.estimates?.longTermCapGainSt)}{' '}
                          &minus;{' '}
                          {formatToFixed(fund?.estimates?.longTermCapGainEnd)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
          {
            name: 'totalCapGain',
            className:
              'Table-header u-textLeft sorting' +
              (selectedSort === 'totalCapGain'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Total Capital Gains: activate to sort column',
            content: (
              <>
                Total Capital
                <br />
                Gains ($)
              </>
            ),
            button: {
              className:
                'Table-headerNode u-flex u-padding2gu u-paddingRight3gu u-textLeft' +
                (selectedSort === 'totalCapGain' ? ' Icon--before' : ''),
              sort: 'totalCapGain',
              sortType: 'number',
            },
            cell: {
              className: ' u-padding0',
              content: (fund) => {
                const hasFundDistributions = hasFundDistributionsEstimates(
                  fund,
                  'distributions'
                )
                const hasFundEstimates = hasFundDistributionsEstimates(
                  fund,
                  'estimates'
                )

                return (
                  <div className="u-flex u-flexCol">
                    {hasFundDistributions && (
                      <div className="u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu">
                        <div>&nbsp;</div>

                        {fund.distributions &&
                          fund.distributions.map((value, index) => {
                            return (
                              <div className="u-paddingTop2gu" key={index}>
                                {formatToFixed(value.totalCapGain, 5)}
                              </div>
                            )
                          })}
                      </div>
                    )}
                    {hasFundEstimates && (
                      <div
                        className={`u-flex u-flexCol u-paddingVert2gu u-paddingRight2gu ${
                          hasFundDistributions
                            ? 'u-borderColorNeutralLight u-borderTop'
                            : ''
                        }`}
                      >
                        <div>&nbsp;</div>
                        <div className="u-paddingTop2gu u-textNoWrap">
                          {formatToFixed(fund?.estimates?.totalCapGainSt)}{' '}
                          &minus;{' '}
                          {formatToFixed(fund?.estimates?.totalCapGainEnd)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
          {
            name: 'totalCapGainPct',
            className:
              'Table-header u-textLeft sorting' +
              (selectedSort === 'totalCapGainPct'
                ? selectedSortDirection
                  ? ' is-ascending'
                  : ' is-descending'
                : ''),
            ariaLabel: 'Percent of Record Date NAV: activate to sort column',
            content: (
              <>
                % of Record
                <br />
                Date NAV
              </>
            ),
            button: {
              className:
                'Table-headerNode u-flex u-padding2gu u-paddingRight3gu u-textLeft' +
                (selectedSort === 'totalCapGainPct' ? ' Icon--before' : ''),
              sort: 'totalCapGainPct',
              sortType: 'number',
            },
            cell: {
              className: ' u-padding0',
              content: (fund) => {
                const hasFundDistributions = hasFundDistributionsEstimates(
                  fund,
                  'distributions'
                )
                const hasFundEstimates = hasFundDistributionsEstimates(
                  fund,
                  'estimates'
                )

                return (
                  <div className="u-flex u-flexCol">
                    {hasFundDistributions && (
                      <div className="u-flex u-flexCol u-padding2gu">
                        <div>&nbsp;</div>

                        {fund.distributions &&
                          fund.distributions.map((value, index) => {
                            return (
                              <div className="u-paddingTop2gu" key={index}>
                                {formatToFixed(value.totalCapGainPct)}
                              </div>
                            )
                          })}
                      </div>
                    )}
                    {hasFundEstimates && (
                      <div
                        className={`u-flex u-flexCol u-padding2gu ${
                          hasFundDistributions
                            ? 'u-borderColorNeutralLight u-borderTop'
                            : ''
                        }`}
                      >
                        <div>&nbsp;</div>
                        <div className="u-paddingTop2gu u-textNoWrap">
                          {formatToFixed(fund?.estimates?.totalCapGainPctSt)}{' '}
                          &minus;{' '}
                          {formatToFixed(fund?.estimates?.totalCapGainPctEnd)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              },
            },
          },
        ],
      },
    },
  ]
  const _aspect = _aspects.find((a) => a.name === section)?.info
  return _aspect
}

export const renderColumnHeader = (
  column,
  selectedSortDirection = true,
  onClickSort = () => {}
) => {
  return (
    <th
      key={column.name}
      className={column.className + ' u-bgColorBrandDark'}
      role="columnheader"
      aria-label={column.ariaLabel}
    >
      {column.button ? (
        <button
          className={column.button.className}
          data-sort={column.button.sort}
          data-sort-type={column.button.sortType}
          data-sort-direction={
            typeof column.button.sortDirection !== 'undefined'
              ? column.button.sortDirection
                ? 1
                : 0
              : selectedSortDirection
                ? 1
                : 0
          }
          onClick={onClickSort}
        >
          {column.content}
        </button>
      ) : (
        <>{column.content}</>
      )}
    </th>
  )
}

export const renderColumnCell = (column, fund) => {
  return (
    <td
      key={column.key + fund.cusip}
      tabIndex="1"
      className={'Table-cell' + (column.cell.className || '')}
    >
      {column.cell.content(fund, column)}
    </td>
  )
}

export const sortFunds = (
  fundsArray,
  selectedSortDirection,
  selectedSortType,
  selectedSort
) => {
  fundsArray.sort((fund1, fund2) => {
    const a = selectedSortDirection ? fund1 : fund2
    const b = selectedSortDirection ? fund2 : fund1

    // Sort by selected sort
    switch (selectedSortType) {
      case 'number': {
        const aValue =
          typeof a[selectedSort] === 'string'
            ? parseFloat(a[selectedSort] === '-' ? 0.0 : a[selectedSort])
            : a[selectedSort]
        const bValue =
          typeof b[selectedSort] === 'string'
            ? parseFloat(b[selectedSort] === '-' ? 0.0 : b[selectedSort])
            : b[selectedSort]

        const numberCompare = aValue < bValue ? -1 : aValue > bValue ? 1 : 0

        // Sort by Fund Name second if necessary (always ascending)
        if (numberCompare) {
          return numberCompare
        } else {
          return (fund1['fundName'] || '').localeCompare(
            fund2['fundName'] || '',
            'en',
            { sensitivity: 'base' }
          )
        }
      }
      case 'array':
        return (a[selectedSort][0] || '').localeCompare(
          b[selectedSort][0] || '',
          'en',
          { sensitivity: 'base' }
        )
      default:
        return (a[selectedSort] || '').localeCompare(
          b[selectedSort] || '',
          'en',
          { sensitivity: 'base' }
        )
    }
  })

  return fundsArray
}

const renderStars = (rating, count) => {
  if (!rating || !count) {
    return '-'
  }
  const getStars = () => {
    return [1, 2, 3, 4, 5].map((stars) => {
      if (rating >= stars) {
        return (
          <span
            key={'star' + stars}
            aria-hidden="true"
            className="Icon u-textSizeMsrp1 u-textColorBrandLight"
          >
            &#x2605;
          </span>
        )
      } else {
        return (
          <span
            key={'star' + stars}
            aria-hidden="true"
            className="Icon u-textSizeMsrp1 u-textColorBrandLight"
          >
            &#x2606;
          </span>
        )
      }
    })
  }

  let _rating = rating

  if (typeof _rating !== 'number') {
    _rating = _rating ? parseInt(_rating) || 0 : 0
  }

  return (
    <div className="u-textNoWrap">
      {typeof _rating !== 'number' ? (
        _rating
      ) : (
        <>
          {getStars(_rating)}
          <br />
          <div className="u-textNoWrap">{count || 0} Funds</div>
        </>
      )}
    </div>
  )
}

export const downloadCapGains = (event, type = 'intervalProductSummary') => {
  event.preventDefault()

  const capGainsParams = {
    type,
    period: null,
    className: null,
  }

  const _url = getDownloadCapGains(capGainsParams)

  window.location = encodeForURL(_url)
}
